import {
  compareAgentLabels,
  compareCommissionContracts,
} from "../../sortHelpers";

/**
 * Group agent contracts, bonuses, chargebacks, and adjustments by agent.
 * Returns an array sorted alphabetically by agent label, with per-agent totals.
 */
export function groupByAgent(run) {
  if (!run) return [];

  const groups = new Map();
  const nameToAgentKey = new Map();

  const normalizeName = (name) =>
    String(name ?? "")
      .trim()
      .toLowerCase();

  const getGroupKey = ({ agentId, agentName, fallbackPrefix }) => {
    if (agentId != null) return `agent:${agentId}`;

    const normalizedName = normalizeName(agentName);
    if (normalizedName) {
      const existingAgentKey = nameToAgentKey.get(normalizedName);
      if (existingAgentKey) return existingAgentKey;
      return `${fallbackPrefix}:${normalizedName}`;
    }

    return `${fallbackPrefix}:unknown`;
  };

  const ensureGroup = (key, agentLabel) => {
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        agentLabel,
        contracts: [],
        bonuses: [],
        chargebacks: [],
        adjustments: [],
      });
    }
    return groups.get(key);
  };

  run.agentContracts.forEach((ac) => {
    const agentName = ac.commissionRecords[0]?.agentName;
    const label = agentName || `Agent #${ac.agentId}`;
    const key = getGroupKey({
      agentId: ac.agentId,
      agentName,
      fallbackPrefix: "contract",
    });
    ensureGroup(key, label).contracts.push(ac);

    const normalizedName = normalizeName(agentName);
    if (key.startsWith("agent:") && normalizedName) {
      nameToAgentKey.set(normalizedName, key);
    }
  });

  run.bonusRecords.forEach((r) => {
    const label = r.agentName || "Unknown agent";
    const key = getGroupKey({
      agentId: r.agentId,
      agentName: r.agentName,
      fallbackPrefix: "bonus",
    });
    ensureGroup(key, label).bonuses.push(r);
  });

  run.chargebackRecords.forEach((r) => {
    const label = r.agentName || "Unknown agent";
    const key = getGroupKey({
      agentId: r.agentId,
      agentName: r.agentName,
      fallbackPrefix: "chargeback",
    });
    ensureGroup(key, label).chargebacks.push(r);
  });

  run.agentAdjustments.forEach((a) => {
    const label =
      a.agentName ||
      (a.agentId != null ? `Agent #${a.agentId}` : "Unknown agent");
    const key = getGroupKey({
      agentId: a.agentId,
      agentName: a.agentName,
      fallbackPrefix: "adj",
    });
    ensureGroup(key, label).adjustments.push(a);
  });

  return Array.from(groups.values())
    .map((group) => {
      const commissions = group.contracts
        .filter((ac) => ac.status !== "declined")
        .reduce((acc, ac) => acc + sum(ac.commissionRecords, "amount"), 0);
      const payoutRunChargebacks = sum(
        group.chargebacks.filter((c) => c.status !== "declined"),
        "amount",
      );
      const adjustments =
        group.contracts
          .filter((ac) => ac.status !== "declined")
          .reduce(
            (acc, ac) => acc + sum(ac.contractAdjustments, "adjustmentAmount"),
            0,
          ) +
        group.bonuses
          .filter((ac) => ac.status !== "declined")
          .reduce(
            (acc, b) =>
              acc +
              (b.adjustments ?? []).reduce(
                (s, a) => s + (a.adjustmentAmount ?? 0),
                0,
              ),
            0,
          ) +
        group.chargebacks
          .filter((c) => c.status !== "declined")
          .reduce(
            (acc, c) => acc + sum(c.adjustments, "adjustmentAmount"),
            0,
          ) +
        sum(group.adjustments, "adjustmentAmount");
      const bonuses = sum(
        group.bonuses.filter((ac) => ac.status !== "declined"),
        "amount",
      );
      return {
        ...group,
        contracts: [...group.contracts].sort(compareContracts),
        totals: {
          commissions,
          bonuses,
          chargebacks: payoutRunChargebacks,
          adjustments,
          net: commissions + bonuses + payoutRunChargebacks + adjustments,
        },
      };
    })
    .sort((a, b) => compareAgentLabels(a.agentLabel, b.agentLabel));
}

// Contracts within a group already share the same agent; sorting here covers
// the tie-break order once groups are ordered by agent full name above.
function compareContracts(a, b) {
  return compareCommissionContracts(a, b, {
    signed: (record) => record?.signed,
    providerNumber: (record) => record?.providerNumber,
    contractNumber: (record) => record?.contractNumber,
  });
}

function sum(list, key) {
  return (list ?? []).reduce((acc, r) => acc + (r[key] ?? 0), 0);
}
